import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs, startAfter, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { Filter, User, Calendar, Activity, Eye, Edit, LogIn, FileText, Clock, RefreshCw } from 'lucide-react';
import BBALoader from '../../components/BBALoader';
import { PinnableCard, ReportBackdrop } from '../../components/crm/reportCards';
import {
  GLASS_TEXT,
  GLASS_FIELD,
  GLASS_TH,
  GLASS_ROW,
  GLASS_SECONDARY_BUTTON,
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassCardHeader,
  GlassAvatar,
  GlassEmpty,
} from '../../components/glass/GlassUi';

const STAFF_ACTIVITY_COLLECTION = 'staff_activity';
const PAGE_SIZE = 50;

// Glass data tones per action (spelled out so Tailwind's JIT keeps them)
const LAVENDER_TEXT = 'text-[#5B43A8] dark:text-[#B9A3F5]';

const ACTION_TYPE_LABELS = {
  login: { label: 'CRM Login', icon: LogIn, color: GLASS_TEXT.mint },
  account_view: { label: 'Account View', icon: Eye, color: GLASS_TEXT.blue },
  note_added: { label: 'Note Added', icon: Edit, color: LAVENDER_TEXT },
  note_edited: { label: 'Note Edited', icon: Edit, color: LAVENDER_TEXT },
  stage_changed: { label: 'Stage Changed', icon: Activity, color: GLASS_TEXT.amber },
  status_changed: { label: 'Status Changed', icon: Activity, color: GLASS_TEXT.amber },
  hours_logged: { label: 'Hours Logged', icon: Clock, color: GLASS_TEXT.mint },
  doc_uploaded: { label: 'Document Uploaded', icon: FileText, color: GLASS_TEXT.blue },
  account_updated: { label: 'Account Updated', icon: Edit, color: GLASS_TEXT.stone },
};

const FIELD_LABEL = 'mb-1 flex items-center gap-1 text-sm font-medium text-gray-600 dark:text-white/60';

function formatTimestamp(ts) {
  if (!ts) return '—';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return format(date, 'MMM d, yyyy h:mm a');
}

function formatDateForInput(date) {
  return format(date, 'yyyy-MM-dd');
}

const StaffActivity = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);

  const [filters, setFilters] = useState({
    userId: '',
    actionType: '',
    fromDate: formatDateForInput(subDays(new Date(), 7)),
    toDate: formatDateForInput(new Date()),
  });

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const loadInternalUsers = async () => {
      try {
        const snap = await getDocs(collection(db, 'internal_users'));
        const users = snap.docs.map(doc => ({
          uid: doc.id,
          ...doc.data(),
          displayName: [doc.data().firstName, doc.data().lastName].filter(Boolean).join(' ') || doc.data().email || doc.id,
        }));
        users.sort((a, b) => a.displayName.localeCompare(b.displayName));
        setInternalUsers(users);
      } catch (error) {
        console.error('Error loading internal users:', error);
      }
    };
    loadInternalUsers();
  }, []);

  const loadActivities = useCallback(async (resetPagination = true) => {
    setLoading(true);
    try {
      const constraints = [orderBy('timestamp', 'desc'), limit(PAGE_SIZE)];

      if (filters.userId) {
        constraints.unshift(where('actorUid', '==', filters.userId));
      }

      if (filters.actionType) {
        constraints.unshift(where('actionType', '==', filters.actionType));
      }

      if (filters.fromDate) {
        const fromTs = Timestamp.fromDate(startOfDay(new Date(filters.fromDate)));
        constraints.push(where('timestamp', '>=', fromTs));
      }

      if (filters.toDate) {
        const toTs = Timestamp.fromDate(endOfDay(new Date(filters.toDate)));
        constraints.push(where('timestamp', '<=', toTs));
      }

      if (!resetPagination && lastDoc) {
        constraints.push(startAfter(lastDoc));
      }

      const q = query(collection(db, STAFF_ACTIVITY_COLLECTION), ...constraints);
      const snap = await getDocs(q);

      const items = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (resetPagination) {
        setActivities(items);
        setPage(1);
      } else {
        setActivities(prev => [...prev, ...items]);
      }

      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (error) {
      console.error('Error loading staff activity:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, lastDoc]);

  useEffect(() => {
    loadActivities(true);
  }, [filters.userId, filters.actionType, filters.fromDate, filters.toDate]);

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      setPage(p => p + 1);
      loadActivities(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setLastDoc(null);
  };

  const handleRefresh = () => {
    setLastDoc(null);
    loadActivities(true);
  };

  const handleAccountClick = (accountId, route) => {
    if (route) {
      navigate(route);
    } else if (accountId) {
      navigate(`/crm/client/${accountId}`);
    }
  };

  const userMap = useMemo(() => {
    const map = {};
    internalUsers.forEach(u => {
      map[u.uid] = u;
    });
    return map;
  }, [internalUsers]);

  const getActorDisplay = (activity) => {
    const user = userMap[activity.actorUid];
    if (user) {
      return user.displayName;
    }
    return activity.actorName || activity.actorEmail || activity.actorUid || 'Unknown';
  };

  const actionTypes = Object.keys(ACTION_TYPE_LABELS);

  return (
    <GlassPage backdrop={<ReportBackdrop />}>
      <GlassPageHeader
        onBack={() => navigate(-1)}
        title={<>Staff<br />Activity</>}
        controls={(
          <>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`${GLASS_SECONDARY_BUTTON} ${showFilters ? 'border-[#F80707]/40 text-[#F80707] dark:border-[#F80707]/50 dark:text-[#FF4D40]' : ''}`}
            >
              <Filter className="h-4 w-4" />
              Filters
            </button>
            <button type="button" onClick={handleRefresh} className={GLASS_SECONDARY_BUTTON}>
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </>
        )}
      />

      <div className="space-y-4">
        {showFilters && (
          <GlassCard className="p-5" delay={0}>
            <GlassCardHeader title="Filters" />
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4">
              <div>
                <label className={FIELD_LABEL}>
                  <User className="h-4 w-4" />
                  Staff Member
                </label>
                <select
                  value={filters.userId}
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                  className={`${GLASS_FIELD} dark:[&>option]:bg-[#14110f]`}
                >
                  <option value="">All Staff</option>
                  {internalUsers.map(user => (
                    <option key={user.uid} value={user.uid}>
                      {user.displayName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={FIELD_LABEL}>
                  <Activity className="h-4 w-4" />
                  Action Type
                </label>
                <select
                  value={filters.actionType}
                  onChange={(e) => handleFilterChange('actionType', e.target.value)}
                  className={`${GLASS_FIELD} dark:[&>option]:bg-[#14110f]`}
                >
                  <option value="">All Actions</option>
                  {actionTypes.map(type => (
                    <option key={type} value={type}>
                      {ACTION_TYPE_LABELS[type].label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={FIELD_LABEL}>
                  <Calendar className="h-4 w-4" />
                  From Date
                </label>
                <input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                  className={`${GLASS_FIELD} dark:[color-scheme:dark]`}
                />
              </div>

              <div>
                <label className={FIELD_LABEL}>
                  <Calendar className="h-4 w-4" />
                  To Date
                </label>
                <input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange('toDate', e.target.value)}
                  className={`${GLASS_FIELD} dark:[color-scheme:dark]`}
                />
              </div>
            </div>
          </GlassCard>
        )}

        <PinnableCard id="activity-log" title="Activity Log" size="wide">
        <GlassCard className="p-5" delay={showFilters ? 80 : 0}>
          <GlassCardHeader title="Activity Log" />
          <p className="mt-1 text-xs text-gray-500 dark:text-white/[0.45]">
            Track CRM logins, account views, and changes by internal team members
          </p>
          {loading && activities.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <BBALoader size={96} />
            </div>
          ) : activities.length === 0 ? (
            <GlassEmpty className="flex-col py-20">
              <Activity className="mx-auto mb-4 h-12 w-12 opacity-50" />
              <p>No activity found for the selected filters.</p>
            </GlassEmpty>
          ) : (
            <>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr>
                      <th className={GLASS_TH}>Timestamp</th>
                      <th className={GLASS_TH}>Staff Member</th>
                      <th className={GLASS_TH}>Action</th>
                      <th className={GLASS_TH}>Details</th>
                      <th className={GLASS_TH}>Account</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700 dark:text-white/75">
                    {activities.map((activity) => {
                      const actionMeta = ACTION_TYPE_LABELS[activity.actionType] || {
                        label: activity.actionType,
                        icon: Activity,
                        color: GLASS_TEXT.stone,
                      };
                      const Icon = actionMeta.icon;

                      return (
                        <tr key={activity.id} className={GLASS_ROW}>
                          <td className="whitespace-nowrap py-3 pr-4 tabular-nums text-gray-500 dark:text-white/50">
                            {formatTimestamp(activity.timestamp)}
                          </td>
                          <td className="whitespace-nowrap py-3 pr-4">
                            <div className="flex items-center gap-3">
                              <GlassAvatar name={getActorDisplay(activity)} />
                              <div>
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {getActorDisplay(activity)}
                                </div>
                                {activity.actorEmail && activity.actorEmail !== getActorDisplay(activity) && (
                                  <div className="text-xs text-gray-500 dark:text-white/40">
                                    {activity.actorEmail}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap py-3 pr-4">
                            <div className="flex items-center gap-2">
                              <Icon className={`h-4 w-4 ${actionMeta.color}`} />
                              <span className="text-gray-900 dark:text-white">
                                {actionMeta.label}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 pr-4">
                            <div className="max-w-md truncate">
                              {activity.summary || '—'}
                            </div>
                          </td>
                          <td className="whitespace-nowrap py-3">
                            {activity.accountId ? (
                              <button
                                type="button"
                                onClick={() => handleAccountClick(activity.accountId, activity.route)}
                                className="font-medium text-[#E00606] hover:underline dark:text-[#FF4D40]"
                              >
                                {activity.accountId.slice(0, 8)}...
                              </button>
                            ) : (
                              <span className="text-gray-400 dark:text-white/30">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {hasMore && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={loading}
                    className={GLASS_SECONDARY_BUTTON}
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}

              <div className="mt-4 border-t border-black/[0.06] pt-4 text-xs text-gray-500 dark:border-white/[0.06] dark:text-white/[0.45]">
                Showing {activities.length} entries
              </div>
            </>
          )}
        </GlassCard>
        </PinnableCard>
      </div>
    </GlassPage>
  );
};

export default StaffActivity;
